
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import depotMasterService from "../../../services/masters/depotMaster.Service";
import ReusableSnackbar from "../../reusableComponent/ReusableSnackbar";
import plantMasterService from "../../../services/masters/plantMaster.Service";
import accountManagerMasterService from "../../../services/masters/accountManagerMaster.Service";

// ── CSS Styles (same as PlantAdd) ──────────────────────────────────────────

const pageSx = {
  background: "#F4F7FB",
  minHeight: "100vh",
  padding: "20px 24px 40px",
  fontFamily: "'Inter', Arial, sans-serif",
};

const breadcrumbSx = {
  fontSize: "12px",
  color: "#718096",
  mb: 2,
  display: "flex",
  alignItems: "center",
  gap: "6px",
};

const pageHeaderSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  mb: 2.5,
};

const pageTitleSx = {
  fontSize: "16px",
  fontWeight: 700,
  color: "#000000",
  display: "flex",
  alignItems: "center",
  gap: "10px",
};

const backBtnSx = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  fontSize: "13px",
  fontWeight: 600,
  color: "#2B6CB0",
  padding: "7px 14px",
  border: "1.5px solid #2B6CB0",
  borderRadius: "7px",
  background: "#fff",
  cursor: "pointer",
  textTransform: "none",
  "&:hover": { background: "#E6F1FB" },
};

const formCardSx = {
  background: "#fff",

  border: "1px solid #E2E8F0",
  overflow: "hidden",
  mb: 3,
};

const sectionHeaderSx = {
  background: "#1C3A5E",
  padding: "12px 22px",
  display: "flex",
  alignItems: "center",
  gap: "10px",
};

const sectionHeaderTitleSx = {
  fontSize: "13px",
  fontWeight: 600,
  color: "#fff",
  letterSpacing: "0.3px",
};

const formBodySx = { padding: "22px 24px" };

const fieldLabelSx = {
  fontSize: "12px",
  fontWeight: 600,
  color: "#2063b6",
  mb: "4px",
  display: "flex",
  alignItems: "center",
  gap: "4px",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "13px",
    fontFamily: "'Inter', Arial, sans-serif",
    background: "#fafafa",
    height: "38px",
    "& fieldset": { borderColor: "#E2E8F0", borderWidth: "1.5px" },
    "&:hover fieldset": { borderColor: "#CBD5E0" },
    "&.Mui-focused fieldset": { borderColor: "#2B6CB0", borderWidth: "2px" },
    "&.Mui-focused": { background: "#fff" },
    "&.Mui-disabled": { background: "#F8FAFC" },
  },
  "& input": {
    color: "#1A202C",
    fontSize: "13px",
    fontFamily: "'Inter', Arial, sans-serif",
    "&::placeholder": { color: "#A0AEC0", opacity: 1 },
  },
  "& .MuiInputLabel-root": { display: "none" },
};

const formFooterSx = {
  padding: "18px 24px",
  background: "#F8FAFC",
  borderTop: "1px solid #E2E8F0",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "10px",
};

const saveBtnSx = {
  display: "flex",
  alignItems: "center",
  gap: "7px",
  padding: "0 22px",
  height: "40px",
  background: "#4CAF50",
  color: "#fff",
  border: "none",
  fontSize: "14px",
  fontWeight: 600,
  textTransform: "none",
  fontFamily: "'Inter', Arial, sans-serif",
  "&:hover": { background: "#028b19", transform: "translateY(-1px)" },
  "&:active": { transform: "translateY(0)" },
  "&.Mui-disabled": { background: "#94a3b8", color: "#fff" },
  transition: "all 0.15s ease",
};

const cancelBtnSx = {
  display: "flex",
  alignItems: "center",
  gap: "7px",
  padding: "0 22px",
  height: "40px",
  background: "#E53E3E",
  color: "#ffffff",
  border: "none",
  "&:hover": { background: "#971f16", transform: "translateY(-1px)" },
  fontSize: "14px",
  fontWeight: 600,
  borderRadius: "8px",
};

// ─────────────────────────────────────────────────────────────────────────────

const DepotAdd = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const login_details = sessionStorage.getItem("loginUser");
  const userD = JSON.parse(login_details);
  const userInfo = userD.result.userInfo;

  const { handleSubmit } = useForm();
  const urlLocation = useLocation();
  const editInfo = urlLocation.state;
  const editData = editInfo && editInfo;

  const [data, setData] = useState({
    depot_id: editData ? editData.depot_id : "",
    depot_name: editData ? editData.depot_name : "",
    depot_address: editData ? editData.depot_address : "",
    contact_person: editData ? editData.contact_person : "",
    contact_no: editData ? editData.contact_no : "",
    sales_officer_id: editData ? editData.sales_officer_id : "",
    inside_plant: editData ? editData.inside_plant : "",
    plant_id: editData ? editData.plant_id : "",
    short_code: editData ? editData.short_code : "",
  });

  const [plant, setPlant] = useState([]);
  const [sales, setSales] = useState([]);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    async function fetchData() {
      try {
        const [plantData, salesData] = await Promise.all([
          plantMasterService.getPlantList({ page: 0, size: 10, searchKey: "" }),
          accountManagerMasterService.getAccountManagerList({ page: 0, size: 10, searchKey: "" }),
        ]);
        setPlant(plantData);
        setSales(salesData);
      } catch (error) {
        console.error("Data fetching failed", error);
        setSuccessMessage("Error fetching dropdown data");
      }
    }
    fetchData();
  }, []);

  const handleChange = ({ target }) => {
    const value = target.value;
    setData((prev) => ({
      ...prev,
      [target.name]: value,
    }));
  };

  const handleBack = () => navigate(-1);

  const onSubmit = async () => {
    const isEditMode = !!editData;
    const depotData = {
      ...data,
      modified_at: isEditMode ? dayjs().format("YYYY-MM-DD HH:mm:ss") : null,
      modified_by: isEditMode ? userInfo.user_id : null,
      created_at: isEditMode ? editData.created_at : dayjs().format("YYYY-MM-DD HH:mm:ss"),
      created_by: isEditMode ? editData.created_by : userInfo.user_id,
      changedUpdatedValue: isEditMode ? "edit" : "create",
    };
    try {
      if (editData) {
        await depotMasterService.editDeleteDepot(depotData);
      } else {
        await depotMasterService.addDepot(depotData);
      }
      setSuccessMessage(editData ? "Updated successfully" : "Added successfully");
      setTimeout(() => navigate(-1), 1000);
    } catch (error) {
      console.error(error);
      setSuccessMessage("Error occurred");
    }
  };

  return (
    <Box sx={pageSx}>

      {/* Breadcrumb */}
      <Box sx={breadcrumbSx}>
        <span>Dashboard</span> / <span style={{ color: "#2B6CB0", fontWeight: 600 }}>Depot Master</span>
      </Box>

      {/* Page Header */}
      <Box sx={pageHeaderSx}>
        <Box sx={pageTitleSx}>
          <WarehouseIcon sx={{ fontSize: 20, color: "#1C3A5E" }} />
          {editData ? "UPDATE" : "CREATE"} DEPOT MASTER
        </Box>
        <Button onClick={handleBack} sx={backBtnSx}>
          <ArrowBackIcon sx={{ fontSize: 16 }} /> Back
        </Button>
      </Box>

      {/* Main Form Card */}
      <Box sx={formCardSx}>

        {/* Navy Strip Header */}
        <Box sx={sectionHeaderSx}>
          <WarehouseIcon sx={{ fontSize: 18, color: "#fff" }} />
          <Typography sx={sectionHeaderTitleSx}>Depot Information</Typography>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={formBodySx}>
            <Grid container spacing={3}>

              {/* Depot Name */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography sx={fieldLabelSx}>
                    Depot Name <span style={{ color: "#E53E3E" }}>*</span>
                  </Typography>
                  <TextField
                    size="small" fullWidth type="text"
                    onChange={handleChange} name="depot_name"
                    value={data.depot_name} required sx={inputSx}
                    placeholder="Enter depot name"
                  />
                </Box>
              </Grid>

              {/* Depot Address */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography sx={fieldLabelSx}>
                    Depot Address <span style={{ color: "#E53E3E" }}>*</span>
                  </Typography>
                  <TextField
                    size="small" fullWidth type="text"
                    onChange={handleChange} name="depot_address"
                    value={data.depot_address} required sx={inputSx}
                    placeholder="Enter depot address"
                  />
                </Box>
              </Grid>

              {/* Feeding Plant */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography sx={fieldLabelSx}>
                    Feeding Plant <span style={{ color: "#E53E3E" }}>*</span>
                  </Typography>
                  <TextField
                    select size="small" fullWidth
                    value={data.plant_id}
                    onChange={(e) => handleChange({ target: { name: "plant_id", value: e.target.value } })}
                    sx={inputSx}
                  >
                    {plant.map((option) => (
                      <MenuItem key={option.plant_id} value={option.plant_id}
                        sx={{ fontSize: "13px", fontFamily: "'Inter', Arial, sans-serif" }}>
                        {option.plant_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Grid>

              {/* Sales Officer */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography sx={fieldLabelSx}>
                    Sales Officer <span style={{ color: "#E53E3E" }}>*</span>
                  </Typography>
                  <TextField
                    select size="small" fullWidth
                    value={data.sales_officer_id}
                    onChange={(e) => handleChange({ target: { name: "sales_officer_id", value: e.target.value } })}
                    sx={inputSx}
                  >
                    {sales.map((option) => (
                      <MenuItem key={option.sales_officer_id} value={option.sales_officer_id}
                        sx={{ fontSize: "13px", fontFamily: "'Inter', Arial, sans-serif" }}>
                        {option.sales_officer_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Grid>

              {/* Short Code */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography sx={fieldLabelSx}>
                    Short Code <span style={{ color: "#E53E3E" }}>*</span>
                  </Typography>
                  <TextField
                    size="small" fullWidth type="text"
                    onChange={handleChange} name="short_code"
                    value={data.short_code} required sx={inputSx}
                    placeholder="Enter short code"
                  />
                </Box>
              </Grid>

              {/* Contact Person */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography sx={fieldLabelSx}>Contact Person</Typography>
                  <TextField
                    size="small" fullWidth type="text"
                    onChange={handleChange} name="contact_person"
                    value={data.contact_person} sx={inputSx}
                    placeholder="Enter contact person"
                  />
                </Box>
              </Grid>

              {/* Contact No */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography sx={fieldLabelSx}>Contact No</Typography>
                  <TextField
                    size="small" fullWidth type="text"
                    onChange={handleChange} name="contact_no"
                    value={data.contact_no} sx={inputSx}
                    placeholder="Enter contact number"
                  />
                </Box>
              </Grid>

              {/* Inside Plant Checkbox */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography sx={fieldLabelSx}>
                    Inside Plant <span style={{ color: "#E53E3E" }}>*</span>
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", height: "38px" }}>
                    <Checkbox
                      size="small"
                      checked={data.inside_plant === 1}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "inside_plant", value: e.target.checked ? 1 : 0 },
                        })
                      }
                      sx={{
                        color: "#CBD5E0",
                        "&.Mui-checked": { color: "#2B6CB0" },
                        padding: 0,
                      }}
                    />
                    <Typography sx={{ ml: 1, fontSize: "13px", color: "#1A202C" }}>
                      {data.inside_plant === 1 ? "Yes" : "No"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

            </Grid>
          </Box>

          {/* Form Footer */}
          <Box sx={formFooterSx}>
            <Button sx={cancelBtnSx} onClick={handleBack}>
              Cancel
            </Button>
            <Button type="submit" sx={saveBtnSx}>
              <SaveIcon sx={{ fontSize: 18 }} />
              {editData ? "Update" : "Save"}
            </Button>
          </Box>
        </form>
      </Box>

      <ReusableSnackbar
        open={!!successMessage}
        onClose={(event, reason) => {
          if (reason !== "clickaway") setSuccessMessage("");
        }}
        message={successMessage}
        autoHideDuration={2000}
        vertical="top"
        horizontal="right"
      />
    </Box>
  );
};

export default DepotAdd;
